import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import categories from '../data/categories.json';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../providers/AuthProvider';
import { getStripe } from '../providers/getStripe';
import apiService from '../providers/ApiService';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FilterListIcon from '@mui/icons-material/FilterList';
import Chip from '@mui/material/Chip';
import CheckIcon from '@mui/icons-material/Check';

export default function List() {
  const [expanded, setExpanded] = React.useState(false);
  const [data, setData] = React.useState([]);
  const [tier, setTier] = React.useState();
  const [completed, setCompleted] = React.useState([]);
  const [completedWithRatings, setCompletedWithRatings] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [showAnonymous, setShowAnonymous] = React.useState(false); // State for anonymous mode
  const [colorFilter, setColorFilter] = React.useState(null); // Filter by color: 'red', 'yellow', 'green', 'new', or null (no filter)
  const [anchorEl, setAnchorEl] = React.useState(null); // For filter menu
  // const isAdmin = localStorage.getItem('isAdmin') == 'true' ? true : false; // or false to start hidden
  const { isAdmin, email } = useAuth();
  let hasExpired;

  // Filter menu handlers
  const handleFilterMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterSelect = (filter) => {
    setColorFilter(filter);
    handleFilterClose();
  };

  // Color display configuration
  const colorConfig = {
    red: { label: 'Needs Work', color: 'error' },
    yellow: { label: 'Satisfactory', color: 'warning' },
    green: { label: 'Excellent', color: 'success' },
    new: { label: 'New', color: 'default' }
  };

  React.useEffect(() => {
    // getCases();
  }, []); // Runs once when the component mounts

  React.useEffect(() => {
    if (email) {
      // Wait until email is available
      fetchUserTier();
      getCompletedCases();
      // getCases();
    }
  }, [email]); // Runs again when email changes

  React.useEffect(() => {
    if (tier) {
      // Wait until email is available
      getCases();
    }
  }, [tier]); // Runs again when email changes

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const navigate = useNavigate();

  const handleButtonClick = (caseItem) => {
    const filteredCases = filterCasesByCategory(caseItem.caseId, data);
    // Find the index of the clicked case within the filtered array
    const clickedCaseIndex = filteredCases.findIndex((id) => id === caseItem.caseId);

    // Find category name based on caseItem.categoryId
    const category = categories.find((cat) => cat.categoryId === caseItem.categoryId);
    const categoryName = category ? category.title : 'Unknown Category';

    // Navigate to the dynamic URL with the caseId
    navigate(`/case`, {
      state: {
        caseIds: filteredCases,
        index: clickedCaseIndex !== -1 ? clickedCaseIndex : 0,
        hasExpired: hasExpired,
        categoryName: categoryName,
        showAnonymous: showAnonymous // Pass anonymous mode state
      }
    });
  };

  const handleEditClick = async (caseItem) => {
    const editCase = await fetchCase(caseItem.caseId);
    navigate(`/config/${caseItem.caseId}`, {
      state: {
        case: editCase
      }
    });
  };

  const handleNewClick = async () => {
    navigate(`/config`);
  };

  const fetchCase = async (caseId) => {
    try {
      return await apiService.get(`/cases/${caseId}`);
    } catch (error) {
      console.error('Failed to fetch case', error);
      return null;
    }
  };

  const getCases = async () => {
    try {
      const data = await apiService.get(`/cases?tier=${tier}`);
      setData(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch cases', error);
      setLoading(false);
    }
  };

  const getCompletedCases = async () => {
    try {
      const data = await apiService.get(`/user/${email}/cases`);
      console.log('Full user cases response:', data);

      if (data && data.completedCases) {
        console.log('Completed cases array:', data.completedCases);
        console.log('First case ID type:', typeof Object.keys(data.completedCases)[0]);
        setCompleted(Object.keys(data.completedCases)); // Extract the case IDs
        setCompletedWithRatings(data.completedCases); // Store the complete object with ratings
      } else {
        console.log('No completed cases data or empty object');
        setCompleted([]); // Ensure it's initialized as an empty array
        setCompletedWithRatings({});
      }
    } catch (error) {
      console.error('Failed to fetch completed cases', error);
      setCompleted([]); // Ensure it's initialized as an empty array on error
      setCompletedWithRatings({});
    }
  };

  const fetchUserTier = async () => {
    try {
      const data = await apiService.get(`/user/${email}`);

      if (data) {
        setTier(data.tier);
        // Check if the subscription has expired
        hasExpired = checkSubscriptionExpiry(data.subscriptionExpiry);
        if (hasExpired) {
          window.location.href = '/tiers';
        }
      }
    } catch (error) {
      console.error('Failed to fetch user tier', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if subscription has expired
  const checkSubscriptionExpiry = (expiryDate) => {
    if (!expiryDate) return true; // If no expiry date exists, consider it expired

    const currentDate = new Date();
    const expiry = new Date(expiryDate);

    return currentDate > expiry; // Returns true if current date is past expiry date
  };

  const filterCasesByCategory = (selectedCaseId, cases) => {
    const selectedCase = cases.find((caseItem) => caseItem.caseId === selectedCaseId);
    if (!selectedCase) {
      console.error('Case not found');
      return [];
    }
    const { categoryId } = selectedCase;
    return cases
      .filter((caseItem) => caseItem.categoryId === categoryId)
      .map((caseItem) => caseItem.caseId);
  };

  // Helper function to calculate filtered case count for each category
  const getFilteredCaseCount = (categoryId) => {
    return data
      .filter((caseItem) => caseItem.categoryId === categoryId)
      .filter((caseItem) => {
        // Apply color filtering
        if (!colorFilter) return true;
        if (colorFilter === 'new') return !completed.includes(caseItem.caseId);
        return completedWithRatings[caseItem.caseId] === colorFilter;
      }).length;
  };

  if (loading) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5, pt: { xs: 12, sm: 12 } }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading cases...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      id="faq"
      sx={{
        pt: { xs: 12, sm: 12 },
        pb: { xs: 8, sm: 16 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
        flexGrow: 1
      }}>
      {isAdmin && (
        <Button
          variant="contained"
          color="primary"
          onClick={async () => await handleNewClick()}
          sx={{
            width: '20%', // 20% width
            marginLeft: '8px', // Add space between the two buttons
            padding: '10px',
            textTransform: 'none'
          }}>
          Create New Case
        </Button>
      )}
      {/* Header section with responsive layout */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          width: '100%',
          marginBottom: 3,
          gap: 2
        }}>
        {/* Title */}
        <Typography
          component="h2"
          variant="h4"
          color="text.primary"
          sx={{
            textAlign: 'left'
          }}>
          Categories
        </Typography>

        {/* Controls Section */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            width: '100%',
            maxWidth: { sm: '60%', md: '50%', lg: '40%' }
          }}>
          {/* Filter Button - Full width on mobile */}
          <Button
            id="filter-button"
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterMenuOpen}
            aria-controls={anchorEl ? 'filter-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
            fullWidth
            sx={{
              height: '36px',
              minHeight: '36px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
            {colorFilter ? `Filter: ${colorConfig[colorFilter].label}` : 'Filter Cases'}
          </Button>

          {/* Anonymous Mode Toggle - Centered on mobile */}
          <FormControlLabel
            control={
              <Switch
                checked={showAnonymous}
                onChange={(e) => setShowAnonymous(e.target.checked)}
                color="primary"
              />
            }
            label="Anonymous Mode"
            sx={{
              margin: 0,
              alignSelf: { xs: 'center', sm: 'auto' }
            }}
          />
        </Box>
      </Box>

      {/* Filter Menu */}
      <Menu
        id="filter-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleFilterClose}
        MenuListProps={{
          'aria-labelledby': 'filter-button'
        }}
        sx={{ maxWidth: '100%' }}>
        <MenuItem onClick={() => handleFilterSelect(null)} sx={{ padding: '8px 16px' }}>
          <Chip
            label="Show All"
            variant="outlined"
            clickable={false}
            onDelete={colorFilter === null ? () => null : undefined}
            deleteIcon={colorFilter === null ? <CheckIcon /> : undefined}
            sx={{
              width: '100%',
              bgcolor: colorFilter === null ? '#e3f2fd' : undefined,
              borderColor: colorFilter === null ? '#2196f3' : undefined,
              color: colorFilter === null ? '#0d47a1' : undefined,
              fontWeight: colorFilter === null ? 'bold' : 'normal'
            }}
          />
        </MenuItem>
        <MenuItem onClick={() => handleFilterSelect('new')} sx={{ padding: '8px 16px' }}>
          <Chip
            label="New Cases"
            variant="outlined"
            clickable={false}
            onDelete={colorFilter === 'new' ? () => null : undefined}
            deleteIcon={colorFilter === 'new' ? <CheckIcon /> : undefined}
            sx={{
              width: '100%',
              bgcolor: colorFilter === 'new' ? '#e3f2fd' : undefined,
              borderColor: colorFilter === 'new' ? '#2196f3' : undefined,
              color: colorFilter === 'new' ? '#0d47a1' : undefined,
              fontWeight: colorFilter === 'new' ? 'bold' : 'normal'
            }}
          />
        </MenuItem>
        {Object.entries(colorConfig)
          .filter(([key]) => key !== 'new')
          .map(([key, config]) => {
            // Define color mappings for each state
            const chipColors = {
              error: {
                bg: colorFilter === key ? '#ffebee' : '#ffebee',
                border: '#ef5350',
                text: '#c62828',
                hoverBg: '#ffcdd2'
              },
              warning: {
                bg: colorFilter === key ? '#fff8e1' : '#fff8e1',
                border: '#ffb74d',
                text: '#ef6c00',
                hoverBg: '#ffecb3'
              },
              success: {
                bg: colorFilter === key ? '#e8f5e9' : '#e8f5e9',
                border: '#66bb6a',
                text: '#2e7d32',
                hoverBg: '#c8e6c9'
              }
            };

            const colorSet = chipColors[config.color] || {};

            return (
              <MenuItem
                key={key}
                onClick={() => handleFilterSelect(key)}
                sx={{ padding: '8px 16px' }}>
                <Chip
                  label={config.label}
                  variant="outlined"
                  clickable={false}
                  onDelete={colorFilter === key ? () => null : undefined}
                  deleteIcon={colorFilter === key ? <CheckIcon /> : undefined}
                  sx={{
                    width: '100%',
                    bgcolor: colorSet.bg,
                    borderColor: colorSet.border,
                    color: colorSet.text,
                    fontWeight: colorFilter === key ? 'bold' : 'normal',
                    '&:hover': {
                      bgcolor: colorSet.hoverBg
                    }
                  }}
                />
              </MenuItem>
            );
          })}
      </Menu>

      <Box sx={{ width: '100%' }}>
        {categories.map((category, categoryIndex) => {
          // Calculate filtered case count (respecting the current filter)
          const filteredCaseCount = getFilteredCaseCount(category.categoryId);

          return (
            <Accordion
              key={categoryIndex}
              expanded={expanded === `panel${categoryIndex}`}
              onChange={handleChange(`panel${categoryIndex}`)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${categoryIndex}-content`}
                id={`panel${categoryIndex}-header`}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography component="h3" variant="subtitle2">
                  {category.title}
                  {/* <Box sx={{ marginRight: 'auto' }}> */}
                  <Badge
                    badgeContent={filteredCaseCount}
                    color={
                      colorFilter === 'red'
                        ? 'error'
                        : colorFilter === 'yellow'
                          ? 'warning'
                          : colorFilter === 'green'
                            ? 'success'
                            : colorFilter === 'new'
                              ? 'info'
                              : 'primary'
                    }
                    sx={{
                      marginLeft: 2,
                      '& .MuiBadge-badge': {
                        fontSize: '0.75rem',
                        height: '20px',
                        minWidth: '20px'
                      }
                    }}
                  />
                </Typography>
                {/* <Badge
                    badgeContent={
                      data.filter(
                        (caseItem) =>
                          caseItem.categoryId === category.categoryId &&
                          !completed.includes(caseItem.caseId)
                      ).length
                    }
                    color="secondary"
                    sx={{
                      '& .MuiBadge-badge': {
                        marginRight: 2,
                        fontSize: '0.75rem',
                        height: '20px',
                        minWidth: '20px'
                      }
                    }}
                  /> */}
                {/* </Box> */}
              </AccordionSummary>
              <AccordionDetails>
                {data
                  .filter((caseItem) => caseItem.categoryId === category.categoryId)
                  .filter((caseItem) => {
                    // Apply color filtering
                    if (!colorFilter) return true;
                    if (colorFilter === 'new') return !completed.includes(caseItem.caseId);
                    return completedWithRatings[caseItem.caseId] === colorFilter;
                  })
                  .map((caseItem, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        marginBottom: '12px'
                      }}>
                      {/* Main Button (80%) */}
                      <Box sx={{ position: 'relative', width: isAdmin ? '80%' : '100%' }}>
                        <Badge
                          badgeContent={
                            Array.isArray(completed) &&
                            !completed.some((id) => id === caseItem.caseId)
                              ? 'new'
                              : null
                          }
                          color="secondary"
                          sx={{
                            position: 'absolute',
                            top: -5,
                            right: -5,
                            '& .MuiBadge-badge': {
                              fontSize: '0.75rem',
                              height: '20px',
                              minWidth: '30px',
                              backgroundColor: 'red',
                              color: 'white'
                            }
                          }}
                        />{' '}
                        {/* Add a color indicator for completed cases */}
                        {completed.includes(caseItem.caseId) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 5,
                              right: 5,
                              width: 15,
                              height: 15,
                              borderRadius: '50%',
                              bgcolor: completedWithRatings[caseItem.caseId]
                                ? colorConfig[completedWithRatings[caseItem.caseId]]?.color ||
                                  'default'
                                : 'default',
                              zIndex: 1
                            }}
                          />
                        )}{' '}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleButtonClick(caseItem)}
                          sx={{
                            width: '100%', // Ensure full width inside the wrapper
                            padding: '10px',
                            textTransform: 'none',
                            fontSize: '1rem',
                            display: 'block',
                            position: 'relative'
                          }}>
                          {showAnonymous ? caseItem.anonymousTitle : caseItem.title}
                        </Button>
                      </Box>

                      {/* Edit Button (20%) */}
                      {isAdmin && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={async () => await handleEditClick(caseItem)}
                          sx={{
                            width: '20%', // 20% width
                            marginLeft: '8px', // Add space between the two buttons
                            padding: '10px',
                            textTransform: 'none'
                          }}>
                          Edit
                        </Button>
                      )}
                    </Box>
                  ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Container>
  );
}
